import React from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useTranslation } from "shared/hooks/useTranslation";
import { useWallet } from "features/wallet/hooks/useWallet";
import { useUserSession } from "shared/contexts/UserContext";

import WalletEmptyHint from "features/wallet/components/WalletEmptyHint";

export interface LiquidityPoolSelectorProps {
    value: string;
    onChange: (poolId: string) => void;
    currencyFilter?: string;
    size?: "small" | "medium";
    fullWidth?: boolean;
}

/**
 * Dropdown to select a liquidity pool. Optional - includes empty option.
 */
export default function LiquidityPoolSelector({
    value,
    onChange,
    currencyFilter,
    size = "small",
    fullWidth = true,
}: LiquidityPoolSelectorProps) {
    const { t } = useTranslation();
    const { profile } = useUserSession();
    const username = profile?.username ?? null;
    const { pools, loading } = useWallet(username);

    const filteredPools = currencyFilter
        ? pools.filter((p) => p.currency === currencyFilter)
        : pools;

    if (!loading && filteredPools.length === 0) {
        return <WalletEmptyHint />;
    }

    return (
        <FormControl fullWidth={fullWidth} size={size} disabled={loading}>
            <InputLabel id="liquidity-pool-select-label">
                {t("wallet.selectPool")} ({t("wallet.optional")})
            </InputLabel>
            <Select
                labelId="liquidity-pool-select-label"
                label={`${t("wallet.selectPool")} (${t("wallet.optional")})`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <MenuItem value="">
                    <em>{t("wallet.optional")}</em>
                </MenuItem>
                {filteredPools.map((pool) => (
                    <MenuItem key={pool._id} value={pool._id}>
                        {pool.name} ({pool.currency})
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
